import isSameDay from 'date-fns/isSameDay';
import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import {
  SHORT_MONTH_FORMAT,
  UseFormatDateByUserProfileLocation,
  useFormatDateByUserProfileLocation,
  getZonedDate,
} from '@leagueplatform/web-common';

export const RENDER_EVERY_N_DAY = 6;

export function getMonthlyXAxisTicks(data: string[], defaultTimezone?: string) {
  const mostRecentDatum = getZonedDate(data[data.length - 1], defaultTimezone);

  // Only display ticks every n days.
  const ticks = data
    .filter((lastUpdate) => {
      const dayDifference = differenceInCalendarDays(
        mostRecentDatum,
        getZonedDate(lastUpdate, defaultTimezone),
      );
      return dayDifference !== 0 && dayDifference % RENDER_EVERY_N_DAY === 0;
    })
    // Remove duplicate dates when there is a series of metrics within a day.
    .filter(
      (date, index, dates) =>
        !dates
          .slice(index + 1)
          .find((d) =>
            isSameDay(
              getZonedDate(d, defaultTimezone),
              getZonedDate(date, defaultTimezone),
            ),
          ),
    );

  return ticks;
}

export function formatMonthlyTick(
  format: UseFormatDateByUserProfileLocation,
  defaultTimezone?: string,
) {
  const formatTick = (
    timestamp: string,
    index: number,
    dates: { value: string | number | Date }[],
  ) => {
    const shouldShowMonth =
      index === 0 ||
      differenceInCalendarMonths(
        getZonedDate(timestamp, defaultTimezone),
        getZonedDate(dates[index - 1].value as string, defaultTimezone),
      ) > 0;

    const formattedTick = format(
      timestamp,
      shouldShowMonth ? SHORT_MONTH_FORMAT : { day: 'numeric' },
    );

    return formattedTick;
  };

  return formatTick;
}

export function useMonthlyTicks(data: string[], defaultTimezone?: string) {
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();
  const tickFormater = formatMonthlyTick(formatDateWithUserProfileLocation);
  const ticks = getMonthlyXAxisTicks(data, defaultTimezone);
  return [tickFormater, ticks] as const;
}
